import { type PermissionCheck, type PermissionResult } from '@/models/domain/permissions'
import { type User, type UserSection } from '@/models/domain/user'

export function isSysOp(user: User): boolean {
  return user.sys_op
}

export function isSysAdmin(user: User): boolean {
  return user.sys_admin
}

function hasSections(user: User, sections: UserSection[]): boolean {
  if (user.sections_all) return true
  if (!user.sections) return false
  const userSections = user.sections
  return sections.some((section) => userSections.includes(section as string))
}

export interface AccessOptions {
  /**
   * When true, SysAdmin does not bypass the section check — only SysOp does.
   * Mirrors api-core's SectionCheckStrict.
   */
  strict?: boolean
}

export function hasAccess(
  user: User,
  requiredSections: UserSection[],
  options?: AccessOptions
): boolean {
  if (isSysOp(user)) return true
  if (!options?.strict && isSysAdmin(user)) return true
  if (!user.sections) return false
  return hasSections(user, requiredSections)
}

export function hasOnlyAccess(user: User, onlySections: UserSection[]): boolean {
  if (isSysOp(user)) return false
  if (isSysAdmin(user)) return false
  if (!user.sections) return false
  const userSections = user.sections
  const hasAllRequired = onlySections.every((section) => userSections.includes(section as string))
  const hasNoOthers = !user.sections.some(
    (section) => !onlySections.includes(section as unknown as UserSection)
  )
  return hasAllRequired && hasNoOthers
}

export function hasSystemAccess(user: User, systemId: string): boolean {
  if (isSysOp(user)) return true
  if (user.systems_all) return true
  if (!user.systems) return false
  return user.systems.includes(systemId)
}

export function hasLocationAccess(user: User, locationId: string): boolean {
  if (isSysOp(user)) return true
  if (user.locations_all) return true
  if (!user.locations) return false
  return user.locations.includes(locationId)
}

export function hasDbaAccess(user: User, dbaId: string): boolean {
  if (isSysOp(user)) return true
  if (user.dbas_all) return true
  if (!user.dbas) return false
  return user.dbas.includes(dbaId)
}

export function hasDistrictAccess(user: User, districtId: string): boolean {
  if (isSysOp(user)) return true
  if (user.districts_all) return true
  if (!user.districts) return false
  return user.districts.includes(districtId)
}

export function hasKeycloakClaims(user: User, claims: string[]): boolean {
  if (!user.keycloak_claims) return false
  const userClaims = user.keycloak_claims
  return claims.every((claim) => userClaims.includes(claim))
}

function checkUserActive(user: User): PermissionResult | null {
  if (!user.active) return { allowed: false, reason: 'User account is inactive' }
  return null
}

function checkSysOpRequired(user: User, check: PermissionCheck): PermissionResult | null {
  if (!check.requireSysOp) return null
  if (!isSysOp(user)) return { allowed: false, reason: 'SysOp access required' }
  return null
}

function checkSysAdminRequired(user: User, check: PermissionCheck): PermissionResult | null {
  if (!check.requireSysAdmin) return null
  if (isSysOp(user)) return null
  if (!isSysAdmin(user)) return { allowed: false, reason: 'SysAdmin access required' }
  return null
}

function checkRequiredSections(user: User, check: PermissionCheck): PermissionResult | null {
  if (!check.requiredSections || check.requiredSections.length === 0) return null
  if (!hasAccess(user, check.requiredSections, { strict: check.strict }))
    return { allowed: false, reason: 'Missing required sections' }
  return null
}

function checkKeycloakClaims(user: User, check: PermissionCheck): PermissionResult | null {
  if (!check.keycloakClaims || check.keycloakClaims.length === 0) return null
  if (isSysOp(user)) return null
  if (!hasKeycloakClaims(user, check.keycloakClaims)) {
    return {
      allowed: false,
      reason: `Missing required Keycloak claims: ${check.keycloakClaims.join(', ')}`,
    }
  }
  return null
}

const permissionChecks = [
  checkUserActive,
  checkSysOpRequired,
  checkSysAdminRequired,
  checkRequiredSections,
  checkKeycloakClaims,
] as const

export function checkPermission(user: User | null, check: PermissionCheck): PermissionResult {
  if (!user) return { allowed: false, reason: 'User not authenticated' }

  for (const checkFn of permissionChecks) {
    const result = checkFn(user, check)
    if (result) return result
  }

  return { allowed: true }
}
